import React, { useEffect, useRef } from 'react';
import { Rgba } from '../../../../helpers/helpers';
import { determineBackgroundColor, determineFillOpacity } from '../../../helpers';
import { SendAFormInteractive, withSendAFormInteractiveState } from '../../SendAFormInteractiveState';
import { canIUse } from '../../../../helpers/PermissionUtility';
import { Features } from '../../../../helpers/PermissionUtility-types';
import { useAppSelector } from '../../../../hooks/ReduxHooks';
import FieldValidationPopup from '../FieldValidationPopup';
import { getViewPositionsForFieldIds } from '../../FieldValidations/selectors';
import { useActions } from '../../../../hooks/useActions';
import { setHighlight } from '../../../actions';

export type FieldWrapperProps = {
  id?: string;
  fieldId?: string;
  children?: React.ReactNode;
  onMouseDown?: VoidFunc;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  backgroundColor?: Rgba;
  editable?: boolean;
  showValidationPopup?: boolean;
} & SendAFormInteractive;

function FieldWrapper(props: FieldWrapperProps) {
  const {
    id,
    fieldId,
    children,
    onMouseDown,
    x,
    y,
    w,
    h,
    backgroundColor,
    editable,
    interactive,
    sendAFormSelected,
    showValidationPopup,
    onSendAFormFieldClick
  } = props;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const formEditorState = useAppSelector((state) => state.formeditor);
  const setHighlightAction = useActions(setHighlight);
  const highlighted = formEditorState.highlight?.fieldId === fieldId;
  const missingRequiredFieldIds = formEditorState.missingRequiredFieldIds || [];
  const missingRequired = missingRequiredFieldIds.includes(fieldId);

  useEffect(() => {
    const svgElement = svgRef.current;

    if (!svgElement) {
      return;
    }

    const handleMouseDown = () => {
      if (!interactive) {
        onSendAFormFieldClick(fieldId, 'form');
        return;
      }

      if (!editable) {
        return;
      }

      if (onMouseDown) {
        onMouseDown();

        if (canIUse(Features.FieldValidations)) {
          const viewPosition = getViewPositionsForFieldIds(formEditorState, [fieldId]);
          setHighlightAction(viewPosition[0].fieldPosition, viewPosition[0].dataLayoutPosition);
        }
      }
    };

    svgElement.addEventListener('mousedown', handleMouseDown);

    return () => {
      svgElement.removeEventListener('mousedown', handleMouseDown);
    };
  }, [editable, fieldId, formEditorState, interactive, onMouseDown, onSendAFormFieldClick, setHighlightAction]);

  const fill = determineBackgroundColor(editable, interactive, backgroundColor);
  const fillOpacity = determineFillOpacity(editable, interactive, backgroundColor);
  const outline = !sendAFormSelected ? { stroke: '#000', strokeWidth: 3, strokeDasharray: '6,4' } : {};

  return (
    <>
      <svg ref={svgRef} x={x} y={y} width={w} height={h} id={id} data-fieldid={fieldId} data-testid="field-wrapper">
        <g>
          <rect width="100%" height="100%" fill={fill} fillOpacity={fillOpacity} {...(interactive ? {} : outline)} />
          {children}
          {canIUse(Features.FieldValidations) && (
            <FieldValidationPopup
              className="depth-three"
              fieldId={fieldId}
              x={x}
              y={y}
              width={w}
              height={h}
              highlighted={highlighted}
              isMissingRequired={missingRequired}
              containsErrors={missingRequired}
              showValidationPopup={showValidationPopup}
            />
          )}
        </g>
      </svg>
    </>
  );
}

export default withSendAFormInteractiveState(FieldWrapper);
